import { useState } from 'react';
import { ChevronDown, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  ACTIVITY_TEMPLATES,
  INITIAL_TEMPLATE_COUNT,
  sortTemplatesByRelevance,
  type ActivityTemplate,
} from '@/lib/activities/activityTemplates';

interface ActivityTemplatePickerProps {
  offeringKeys?: string[];
  onSelect: (template: ActivityTemplate) => void;
  onBlank: () => void;
}

export function ActivityTemplatePicker({
  offeringKeys = [],
  onSelect,
  onBlank,
}: ActivityTemplatePickerProps) {
  const [showMore, setShowMore] = useState(false);

  const sorted = sortTemplatesByRelevance(ACTIVITY_TEMPLATES, offeringKeys);
  const visible = showMore ? sorted : sorted.slice(0, INITIAL_TEMPLATE_COUNT);
  const hasMore = sorted.length > INITIAL_TEMPLATE_COUNT;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Skapa aktivitet</h1>
        <p className="text-muted-foreground mt-1">
          Välj en startpunkt som passar det ni vill erbjuda. Allt går att ändra efteråt.
        </p>
      </div>

      {/* Template grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {visible.map((t) => (
          <TemplateCard key={t.id} template={t} onSelect={() => onSelect(t)} />
        ))}
      </div>

      {/* Show more */}
      {hasMore && !showMore && (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            className="gap-2 text-muted-foreground"
            onClick={() => setShowMore(true)}
          >
            Visa fler idéer
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Blank option */}
      <div className="border-t pt-4">
        <button
          type="button"
          onClick={onBlank}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <FileText className="h-4 w-4" />
          Börja från tom aktivitet
        </button>
      </div>
    </div>
  );
}

function TemplateCard({
  template,
  onSelect,
}: {
  template: ActivityTemplate;
  onSelect: () => void;
}) {
  const Icon = template.icon;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'group flex items-start gap-3 rounded-lg border border-border bg-card p-4 text-left transition-all',
        'hover:border-primary/40 hover:shadow-sm hover:bg-accent/30',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      )}
    >
      <div className="rounded-md bg-muted p-2 shrink-0 group-hover:bg-primary/10 transition-colors">
        <Icon className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
      </div>
      <div className="min-w-0">
        <span className="block font-medium text-sm">{template.label}</span>
        <span className="block text-xs text-muted-foreground mt-0.5 leading-relaxed">
          {template.description}
        </span>
      </div>
    </button>
  );
}
